import type { MapPoiType } from '@/lib/types/map-poi'
import {
  MARKER_SIZE_PX,
  PIN_COLOR,
  POI_DOT_COLOR,
  POI_MARKER_HEIGHT,
  POI_MARKER_WIDTH,
} from './constants'
import { toExternalUrl } from './poiHelpers'

export type MapHtmlMarker = {
  clear: () => void
}

function createMarkerIcon(
  iconUrl: string | null,
  title: string
): HTMLElement {
  if (iconUrl) {
    const img = document.createElement('img')
    img.src = iconUrl
    img.alt = title
    img.draggable = false
    img.decoding = 'async'
    // Size via CSS only — setting width/height attributes forces a 1× raster
    // and looks soft on retina when the map scales the marker.
    img.style.cssText = `
      display:block;
      width:${MARKER_SIZE_PX}px;
      height:${MARKER_SIZE_PX}px;
      object-fit:contain;
      image-rendering:-webkit-optimize-contrast;
      image-rendering:high-quality;
    `
    return img
  }

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('width', String(MARKER_SIZE_PX))
  svg.setAttribute('height', String(MARKER_SIZE_PX))
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('aria-hidden', 'true')
  svg.style.display = 'block'
  svg.innerHTML = `<path fill="${PIN_COLOR}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>`

  return svg as unknown as HTMLElement
}

export function createMainMarkerContent(
  iconUrl: string | null,
  title: string
): HTMLElement {
  const root = document.createElement('div')
  // Fixed box so the property logo centers on the lat/lng (label floats above).
  root.style.cssText = `
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${MARKER_SIZE_PX}px;
    height: ${MARKER_SIZE_PX}px;
  `

  const labelText = title.trim()
  if (labelText) {
    const label = document.createElement('span')
    label.textContent = labelText
    label.style.cssText = `
      position: absolute;
      left: 50%;
      bottom: calc(100% + 4px);
      transform: translateX(-50%);
      max-width: 160px;
      padding: 2px 8px;
      border-radius: 4px;
      background: #fff;
      box-shadow: 0 1px 4px rgba(0,0,0,0.28);
      color: var(--site-brand-dark-color);
      font: 700 11px/1.3 var(--font-body);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      pointer-events: none;
    `
    root.appendChild(label)
  }

  root.appendChild(createMarkerIcon(iconUrl, title))
  return root
}

export function createPoiDot(color = POI_DOT_COLOR): HTMLElement {
  const div = document.createElement('div')
  div.style.cssText = `
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${color};
    border: 2px solid #fff;
    box-shadow: 0 1px 5px rgba(0,0,0,0.3);
    cursor: pointer;
  `
  return div
}

export function createPoiMarkerContent(
  iconUrl: string | null,
  title: string
): HTMLElement {
  if (iconUrl) {
    const img = document.createElement('img')
    img.src = iconUrl
    img.width = POI_MARKER_WIDTH
    img.height = POI_MARKER_HEIGHT
    img.alt = title
    img.draggable = false
    img.style.cssText = 'display:block;cursor:pointer;object-fit:contain;'
    return img
  }

  // Fallback pill when category has no poiMarker asset yet
  return createPoiDot()
}

function getContentSize(content: HTMLElement): { width: number; height: number } {
  if (content instanceof HTMLImageElement) {
    const styleW = parseInt(content.style.width || '', 10)
    const styleH = parseInt(content.style.height || '', 10)
    return {
      width: content.width || styleW || MARKER_SIZE_PX,
      height: content.height || styleH || MARKER_SIZE_PX,
    }
  }

  const styleW = parseInt(content.style.width || '', 10)
  const styleH = parseInt(content.style.height || '', 10)
  if (styleW && styleH) {
    return { width: styleW, height: styleH }
  }

  const attrW = parseInt(content.getAttribute('width') || '', 10)
  const attrH = parseInt(content.getAttribute('height') || '', 10)
  if (attrW && attrH) {
    return { width: attrW, height: attrH }
  }

  return { width: MARKER_SIZE_PX, height: MARKER_SIZE_PX }
}

export function createOverlay(
  OverlayView: typeof google.maps.OverlayView,
  opts: {
    position: google.maps.LatLngLiteral
    content: HTMLElement
    anchorBottom?: boolean
  }
): google.maps.OverlayView {
  class PinOverlay extends OverlayView {
    private div: HTMLDivElement | null = null
    private readonly pos: google.maps.LatLng
    private readonly content: HTMLElement
    private readonly anchorBottom: boolean

    constructor() {
      super()
      this.pos = new google.maps.LatLng(opts.position.lat, opts.position.lng)
      this.content = opts.content
      this.anchorBottom = opts.anchorBottom ?? false
    }

    onAdd() {
      const div = document.createElement('div')
      div.style.position = 'absolute'
      div.style.overflow = 'visible'
      div.style.pointerEvents = 'auto'
      div.appendChild(this.content)
      this.div = div
      this.getPanes()?.overlayMouseTarget.appendChild(div)
    }

    draw() {
      const projection = this.getProjection()
      const div = this.div
      if (!projection || !div) return

      const point = projection.fromLatLngToDivPixel(this.pos)
      if (!point) return

      const measured = getContentSize(this.content)
      const width = div.offsetWidth || measured.width
      const height = div.offsetHeight || measured.height
      // Round to whole pixels to avoid subpixel blur on the logo.
      div.style.left = `${Math.round(point.x - width / 2)}px`
      div.style.top = this.anchorBottom
        ? `${Math.round(point.y - height)}px`
        : `${Math.round(point.y - height / 2)}px`
    }

    onRemove() {
      this.div?.remove()
      this.div = null
    }
  }

  return new PinOverlay()
}

export function mountHtmlMarker(
  map: google.maps.Map,
  OverlayView: typeof google.maps.OverlayView,
  AdvancedMarkerElement:
    | typeof google.maps.marker.AdvancedMarkerElement
    | undefined,
  opts: {
    position: google.maps.LatLngLiteral
    content: HTMLElement
    title?: string
    anchorBottom?: boolean
    zIndex?: number
    onClick?: () => void
  }
): MapHtmlMarker {
  if (AdvancedMarkerElement) {
    let content = opts.content
    // Center-anchored: 0×0 wrapper on the LatLng; keep transform off the <img>
    // so the logo stays sharp on retina.
    if (!opts.anchorBottom) {
      const wrapper = document.createElement('div')
      wrapper.style.cssText =
        'position:relative;width:0;height:0;overflow:visible;'
      opts.content.style.position = 'absolute'
      opts.content.style.left = '0'
      opts.content.style.top = '0'
      opts.content.style.transform = 'translate(-50%, -50%)'
      wrapper.appendChild(opts.content)
      content = wrapper
    }
    const marker = new AdvancedMarkerElement({
      map,
      position: opts.position,
      title: opts.title ?? '',
      content,
      zIndex: opts.zIndex,
    })
    if (opts.onClick) marker.addListener('click', opts.onClick)
    return {
      clear: () => {
        marker.map = null
      },
    }
  }

  if (opts.onClick) {
    opts.content.style.cursor = 'pointer'
    opts.content.addEventListener('click', opts.onClick)
  }

  const overlay = createOverlay(OverlayView, {
    position: opts.position,
    content: opts.content,
    anchorBottom: opts.anchorBottom,
  })
  overlay.setMap(map)
  return { clear: () => overlay.setMap(null) }
}

export function createPoiInfoContent(poi: MapPoiType): HTMLDivElement {
  const wrapper = document.createElement('div')
  wrapper.style.cssText =
    'width:260px;padding:2px;font-family:Arial,sans-serif'

  const title = document.createElement('h3')
  title.textContent = poi.title ?? 'Nearby place'
  title.style.cssText =
    'margin:0 0 7px;color:#293526;font-size:16px;line-height:22px;font-weight:700'
  wrapper.appendChild(title)

  if (poi.category?.name) {
    const category = document.createElement('span')
    category.textContent = poi.category.name
    category.style.cssText =
      'display:inline-flex;align-items:center;padding:3px 8px;margin-bottom:8px;border:1px solid #ddd8ca;border-radius:999px;background:#fff;color:#5f625b;font-size:9px;line-height:12px;text-transform:uppercase;letter-spacing:.02em'
    wrapper.appendChild(category)
  }

  if (poi.description) {
    const description = document.createElement('p')
    description.textContent = poi.description
    description.style.cssText =
      'margin:0 0 10px;color:#66675f;font-size:12px;line-height:17px'
    wrapper.appendChild(description)
  }

  if (poi.distanceDetails?.text) {
    const distance = document.createElement('span')
    distance.textContent = poi.distanceDetails.text
    distance.style.cssText =
      'display:inline-flex;align-items:center;padding:4px 9px;border:1px solid #ddd8ca;border-radius:999px;background:#fff;color:#293526;font-size:10px;line-height:13px'
    wrapper.appendChild(distance)
  }

  const mapHref = toExternalUrl(poi.mapUrl)
  if (mapHref) {
    const link = document.createElement('a')
    link.href = mapHref
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.textContent = 'View on Google Maps'
    link.style.cssText =
      'display:flex;margin-top:10px;color:#293526;font-size:11px;line-height:15px;font-weight:700;text-decoration:underline;text-underline-offset:2px'
    wrapper.appendChild(link)
  }

  return wrapper
}
